/**
 * EmailSubscriptionSection Component
 *
 * Displays email subscription form with modal dialog
 * Highest priority feature (Priority #1 from constitution)
 */

import React, { useState } from 'react'
import { Box, Container, Typography, Button, Stack } from '@mui/material'
import EmailIcon from '@mui/icons-material/Email'
import { EmailSubscriptionContent } from '@/data/types'
import EmailSubscribeDialog from '../EmailSubscribeDialog'

export interface EmailSubscriptionSectionProps {
  /**
   * Content for the email subscription section
   */
  content: EmailSubscriptionContent

  /**
   * Optional CSS class name for styling
   */
  className?: string

  /**
   * Test ID for automated testing
   */
  testId?: string
}

export const EmailSubscriptionSection: React.FC<
  EmailSubscriptionSectionProps
> = ({ content, className, testId = 'email-subscription-section' }) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const emailHeadingId = 'email-subscription-heading'

  const handleOpenDialog = () => {
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)

    // Note: _onSubscribeSuccess and _onSubscribeError callbacks are available
    // but EmailSubscribeDialog handles submission internally.
    // These props are kept for future integration with analytics/tracking.
  }

  return (
    <>
      <Box
        component="section"
        role="region"
        aria-labelledby={emailHeadingId}
        data-testid={testId}
        className={className}
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: 'grey.50',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <EmailIcon
            sx={{
              fontSize: { xs: 48, md: 64 },
              color: 'primary.main',
              mb: 3,
            }}
          />

          <Typography
            id={emailHeadingId}
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.75rem' },
              fontWeight: 700,
              mb: 3,
              color: 'text.primary',
            }}
          >
            {content.title}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1.125rem', md: '1.25rem' },
              lineHeight: 1.8,
              color: 'text.secondary',
              mb: 2,
              maxWidth: '700px',
              mx: 'auto',
            }}
          >
            {content.description}
          </Typography>

          {content.subscriberCount && (
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                mb: 4,
              }}
            >
              Join {content.subscriberCount.toLocaleString()}+ subscribers
            </Typography>
          )}

          <Stack spacing={2} alignItems="center">
            <Button
              type="button"
              variant="contained"
              color="primary"
              size="large"
              onClick={handleOpenDialog}
              startIcon={<EmailIcon />}
              aria-label="Subscribe to Updates"
              sx={{
                px: 5,
                py: 2,
                fontSize: { xs: '1rem', md: '1.125rem' },
                fontWeight: 600,
                textTransform: 'none',
                minWidth: { xs: '100%', sm: '300px' },
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s ease',
                },
              }}
            >
              Subscribe to Updates
            </Button>

            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                fontStyle: 'italic',
              }}
            >
              {content.privacyStatement}
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* Email Subscription Dialog */}
      <EmailSubscribeDialog open={dialogOpen} onClose={handleCloseDialog} />
    </>
  )
}
